// ─── Coffre quotidien du mode Duel — stockage Supabase (Beta v1) ───────────────
// Supabase = source de vérité (anti-cheat : reset localStorage ne sert à rien).
// L'UI reste instantanée grâce au boot-preload et aux updates locales optimistes.
//
// Colonnes players : duel_chest_date (date), duel_chest_matches (int), duel_chest_claimed (bool)
//
// TODO KST : le reset quotidien est basé sur la date UTC renvoyée par Supabase.
// Pour une beta en Corée, il faudrait utiliser KST (UTC+9). Deux options :
//   a) RPC Supabase `now() AT TIME ZONE 'Asia/Seoul'` → retourne la date KST
//   b) Offset côté client : new Date(now + 9*3600_000) puis extraction de la date
// Pour l'instant on utilise la date UTC du client avec un TODO explicite.

import { sb } from "../../../lib/supabase";

export const MATCHES_FOR_CHEST = 10;

export interface DailyChestState {
  matches: number;   // combats du jour (plafonné à goal dans l'UI)
  goal: number;
  ready: boolean;    // goal atteint, pas encore réclamé
  claimed: boolean;
}

// ── Date du jour ──────────────────────────────────────────────────────────────
// TODO KST : remplacer par date KST pour reset à minuit heure coréenne.
function todayUTC(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

// ── Calcul de l'état depuis les données brutes DB ─────────────────────────────
export function computeChestState(
  dbDate: string | null | undefined,
  dbMatches: number | undefined,
  dbClaimed: boolean | undefined
): DailyChestState {
  const today = todayUTC();
  const isToday = dbDate === today;
  const matches = isToday ? (dbMatches ?? 0) : 0;
  const claimed = isToday ? (dbClaimed ?? false) : false;
  const ready = matches >= MATCHES_FOR_CHEST && !claimed;
  console.log("[DailyChest] compute input", { dbDate, dbMatches, dbClaimed, today, isToday });
  const out = { matches: Math.min(matches, MATCHES_FOR_CHEST), goal: MATCHES_FOR_CHEST, ready, claimed };
  console.log("[DailyChest] compute output", out);
  return out;
}

/**
 * Enregistre un combat lancé. Met à jour Supabase de façon awaitée.
 * Retourne le nouvel état optimiste immédiatement, mais loggue le résultat du patch.
 *
 * Si la date DB est différente d'aujourd'hui → reset complet + matches=1.
 */
export async function recordMatchRemote(
  playerName: string,
  currentState: DailyChestState,
  currentDbDate: string | null | undefined
): Promise<DailyChestState> {
  console.log("[DailyChest/Remote] start", { playerName });

  const today = todayUTC();
  const isToday = currentDbDate === today;

  let newMatches: number;
  let patch: Record<string, unknown>;

  if (!isToday) {
    newMatches = 1;
    patch = { duel_chest_date: today, duel_chest_matches: 1, duel_chest_claimed: false };
    console.log("[DailyChest] reset for new day", { currentDbDate, today });
  } else {
    newMatches = currentState.matches + 1;
    patch = { duel_chest_matches: newMatches };
  }

  console.log("[DailyChest/Remote] patch target", { filter: `name=eq.${playerName}` });
  console.log("[DailyChest/Remote] patch payload", patch);
  console.log("[DailyChest/Remote] db before (from local state)", {
    currentDbDate,
    currentMatches: currentState.matches,
    currentClaimed: currentState.claimed,
    isToday,
  });

  try {
    const enc = encodeURIComponent(playerName);
    const patchResult = await sb.patch("players", `name=eq.${enc}`, patch);
    console.log("[DailyChest/Remote] patch result", { patchResult });
  } catch (error) {
    console.error("[DailyChest/Remote] failed", error);
  }

  const newClaimed = isToday ? currentState.claimed : false;
  const ready = newMatches >= MATCHES_FOR_CHEST && !newClaimed;
  const state = {
    matches: Math.min(newMatches, MATCHES_FOR_CHEST),
    goal: MATCHES_FOR_CHEST,
    ready,
    claimed: newClaimed,
  };
  console.log("[DailyChest/Remote] final state", state);
  return state;
}

/**
 * Réclame le coffre si les conditions sont remplies.
 * TODO anti-cheat : remplacer par RPC Supabase atomique `claim_duel_chest(player_name)`
 * qui vérifie matches >= goal et claimed = false en une seule transaction.
 */
export async function claimChestRemote(
  playerName: string,
  currentState: DailyChestState
): Promise<DailyChestState> {
  if (!currentState.ready) {
    console.log("[DailyChest] claim refused — not ready", { currentState });
    return currentState;
  }
  console.log("[DailyChest/Remote] patch target", { filter: `name=eq.${playerName}` });
  console.log("[DailyChest/Remote] patch payload", { duel_chest_claimed: true });
  try {
    const enc = encodeURIComponent(playerName);
    const patchResult = await sb.patch("players", `name=eq.${enc}`, { duel_chest_claimed: true });
    console.log("[DailyChest/Remote] patch result", { patchResult });
    // sb.patch ne throw pas : en cas d'échec HTTP/réseau il retourne false.
    // On doit donc tester explicitement, sinon on renverrait claimed:true et le
    // pack serait ouvert alors que la DB n'a rien enregistré (→ re-claim possible).
    if (!patchResult) {
      console.warn("[DailyChest/Remote] claim patch a retourné false — DB non mise à jour, claim annulé");
      return currentState;
    }
    console.log("[DailyChest] claim success");
  } catch (error) {
    console.error("[DailyChest/Remote] claim failed", error);
    return currentState;
  }
  return { ...currentState, ready: false, claimed: true };
}
